import { useNavigate } from "@tanstack/react-router";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDemoScheduling } from "../demo/DemoSchedulingProvider";
import { acquireBodyScrollLock } from "../lib/bodyScrollLock";
import { Icon, type IconName } from "./Icon";

interface Command {
  label: string;
  hint: string;
  icon: IconName;
  run: () => void;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const listboxId = useId();
  const navigate = useNavigate();
  const { state: schedulingState } = useDemoScheduling();

  const commands = useMemo<Command[]>(
    () => [
      {
        label: "Go to Today",
        hint: "Clinic queue and rooms",
        icon: "calendar",
        run: () => void navigate({ to: "/" }),
      },
      ...(schedulingState?.configuration.mode === "walk_in_only"
        ? []
        : [
            {
              label: "Open schedule",
              hint: "Day and week physician calendars",
              icon: "clock" as const,
              run: () => void navigate({ to: "/schedule" }),
            },
          ]),
      {
        label: "Open attention inbox",
        hint: "Results, follow-ups, signatures",
        icon: "inbox",
        run: () => void navigate({ to: "/attention" }),
      },
      {
        label: "Open billing workbench",
        hint: "Claims and remittance",
        icon: "billing",
        run: () => void navigate({ to: "/billing" }),
      },
      {
        label: "Create walk-in",
        hint: "Register a walk-in",
        icon: "plus",
        run: () => void navigate({ to: "/" }),
      },
      {
        label: "Scheduling settings",
        hint: "Hours, appointment types, and online booking",
        icon: "settings",
        run: () => void navigate({ to: "/admin/scheduling" }),
      },
      {
        label: "Patient messages",
        hint: "Confirmations, reminders, and their wording",
        icon: "send",
        run: () => void navigate({ to: "/admin/patient-messages" }),
      },
      {
        label: "Template settings",
        hint: "Encounter, letter, referral, and patient instruction templates",
        icon: "document",
        run: () => void navigate({ to: "/admin/templates" }),
      },
      {
        label: "Clinic settings",
        hint: "People, policies, integrations",
        icon: "settings",
        run: () => void navigate({ to: "/admin" }),
      },
    ],
    [navigate, schedulingState?.configuration.mode],
  );

  const filtered = commands.filter((command) =>
    `${command.label} ${command.hint}`.toLowerCase().includes(query.toLowerCase()),
  );
  const selectedIndex = filtered.length ? Math.min(activeIndex, filtered.length - 1) : -1;

  useEffect(() => {
    if (!open) return;

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Counted, not save/restore: a portaled dialog suspending underneath the
    // open palette must not have its "hidden" restored over the page.
    const releaseScrollLock = acquireBodyScrollLock();
    setQuery("");
    setActiveIndex(0);
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 0);

    return () => {
      window.clearTimeout(focusTimer);
      releaseScrollLock();
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [open]);

  const runCommand = (index: number) => {
    const command = filtered[index];
    if (!command) return;
    command.run();
    onClose();
  };

  const handleInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (!filtered.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (Math.min(current, filtered.length - 1) + 1) % filtered.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex(
        (current) =>
          (Math.min(current, filtered.length - 1) - 1 + filtered.length) % filtered.length,
      );
    } else if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(filtered.length - 1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      runCommand(selectedIndex);
    }
  };

  const handleDialogKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(
        'input:not([disabled]), button:not([disabled]):not([tabindex="-1"])',
      ) ?? [],
    );
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  return (
    <div className="dialog-backdrop">
      <section
        ref={dialogRef}
        className="command-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={handleDialogKeyDown}
      >
        <div className="command-search">
          <Icon name="search" />
          <input
            ref={inputRef}
            role="combobox"
            aria-label="Search commands"
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-expanded="true"
            aria-activedescendant={
              selectedIndex >= 0 ? `${listboxId}-option-${selectedIndex}` : undefined
            }
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleInputKeyDown}
            placeholder="Find a patient or run a command…"
          />
          <kbd>Esc</kbd>
          <button
            type="button"
            className="command-close"
            aria-label="Close command palette"
            onClick={onClose}
          >
            <Icon name="close" />
          </button>
        </div>
        <div className="command-results">
          <p className="command-section-label">Quick actions</p>
          <div
            id={listboxId}
            className="command-list"
            role="listbox"
            aria-label="Available commands"
          >
            {filtered.length ? (
              filtered.map((command, index) => (
                <button
                  id={`${listboxId}-option-${index}`}
                  key={command.label}
                  type="button"
                  role="option"
                  aria-selected={index === selectedIndex}
                  tabIndex={-1}
                  className={`command-result ${index === selectedIndex ? "active" : ""}`}
                  onMouseMove={() => setActiveIndex(index)}
                  onClick={() => runCommand(index)}
                >
                  <span className="command-result-icon">
                    <Icon name={command.icon} />
                  </span>
                  <span>
                    <strong>{command.label}</strong>
                    <small>{command.hint}</small>
                  </span>
                  <Icon name="arrow" className="command-arrow" />
                </button>
              ))
            ) : (
              <p className="command-empty">No matching command</p>
            )}
          </div>
          <p className="sr-only" aria-live="polite">
            {filtered.length} commands available
          </p>
        </div>
        <footer className="command-footer">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> navigate
          </span>
          <span>
            <kbd>↵</kbd> open
          </span>
          <span>Clinical workspace</span>
        </footer>
      </section>
    </div>
  );
}
